/**
 * How an account came to exist, as reported on `signup_started` and
 * `signup_completed`.
 *
 * `publicApply` is a method rather than a separate funnel because the public
 * apply flow registers the applicant as part of filing the application: the
 * account step is a signup, it is just not on a signup route.
 */
export const SIGNUP_METHODS = [
  'email',
  'google',
  'outlook',
  'publicApply',
] as const;

export type SignupMethod = (typeof SIGNUP_METHODS)[number];

/**
 * The method reported on `signup_started`, which fires before the visitor has
 * picked one.
 *
 * `unknown` mirrors `ConversionSide`'s: a real outcome, not a defect. A surface
 * that offers more than one way in genuinely does not know which one this
 * visitor will take, and no event stream can attribute the start
 * retrospectively — the choice happens after it.
 */
export type SignupStartMethod = SignupMethod | 'unknown';

/**
 * Derives the `signup_started` method from the methods the surface offers.
 *
 * The offered list is the input rather than a literal because naming the
 * method at the call site is what corrupted this funnel once already: the
 * researcher signup page reported `email` while also offering Google and
 * Outlook, so every social start counted as an email start that never
 * completed. Derived this way, adding or removing a button changes the
 * reported method with it.
 */
export function deriveSignupStartMethod(
  offered: readonly SignupMethod[],
): SignupStartMethod {
  const distinct = new Set(offered);
  const [only] = distinct;
  return distinct.size === 1 && only ? only : 'unknown';
}
